export type ApiErrorCode =
  | 'NETWORK_ERROR'
  | 'HTTP_ERROR'
  | 'VALIDATION_ERROR'
  | 'REQUEST_TIMEOUT'
  | 'PARSE_ERROR'
  | 'ABORTED'
  | (string & {});

export interface ApiProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code?: string;
  correlationId?: string;
  retryable?: boolean;
  stage?: string | null;
  [key: string]: unknown;
}

export interface ApiErrorPayload {
  message: string;
  status: number;
  code: ApiErrorCode;
  correlationId?: string;
  details?: unknown;
  problem?: ApiProblemDetails;
  cause?: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: ApiErrorCode;
  public readonly correlationId?: string;
  public readonly details?: unknown;
  public readonly problem?: ApiProblemDetails;
  public readonly cause?: unknown;

  constructor(payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = payload.status;
    this.code = payload.code;
    this.correlationId = payload.correlationId;
    this.details = payload.details;
    this.problem = payload.problem;
    this.cause = payload.cause;
  }

  static async fromResponse(response: Response, fallbackCorrelationId?: string) {
    const correlationId = response.headers.get('x-correlation-id') ?? fallbackCorrelationId;

    try {
      const data = (await response.json()) as {
        detail?: unknown;
        error?: unknown;
        message?: unknown;
        title?: unknown;
        code?: unknown;
        correlationId?: unknown;
        details?: unknown;
      };
      const problem = isApiProblemDetailsPayload(data) ? data : undefined;

      const message =
        (typeof data.detail === 'string' ? data.detail : undefined) ||
        (typeof data.message === 'string' ? data.message : undefined) ||
        (typeof data.detail === 'string' ? data.detail : undefined) ||
        (typeof data.error === 'string' ? data.error : undefined) ||
        (typeof data.title === 'string' ? data.title : undefined) ||
        response.statusText ||
        'Request failed';
      const code = (typeof data.code === 'string' ? data.code : undefined) || 'HTTP_ERROR';

      return new ApiError({
        message,
        status: response.status,
        code,
        correlationId:
          (typeof data.correlationId === 'string' ? data.correlationId : undefined) ||
          correlationId,
        details: data.details ?? data,
        problem,
      });
    } catch (error) {
      return new ApiError({
        message: response.statusText || 'Request failed',
        status: response.status,
        code: 'HTTP_ERROR',
        correlationId,
        cause: error,
      });
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      code: this.code,
      correlationId: this.correlationId,
      details: this.details,
      problem: this.problem,
    };
  }
}

export function isApiProblemDetailsPayload(value: unknown): value is ApiProblemDetails {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Partial<ApiProblemDetails>;
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.status === 'number'
  );
}

export function isApiError(error: unknown): error is ApiError {
  if (error instanceof ApiError) return true;
  if (typeof error !== 'object' || error === null) return false;

  const candidate = error as Partial<ApiError>;
  return (
    candidate.name === 'ApiError' &&
    typeof candidate.message === 'string' &&
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string'
  );
}

export function isNetworkError(error: unknown): boolean {
  return isApiError(error) && error.code === 'NETWORK_ERROR';
}

export function isTimeoutError(error: unknown): boolean {
  return isApiError(error) && error.code === 'REQUEST_TIMEOUT';
}

export function isRetryableError(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.code === 'NETWORK_ERROR' || error.code === 'REQUEST_TIMEOUT' || error.status >= 500)
  );
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
}
